import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCurrentUser, updateCurrentUser, uploadAvatar } from "../../features/auth/api";
import { IS_API_MODE } from "../../shared/config/runtime";
import { useAuthStore } from "../../features/auth/store";
import { getApiErrorMessage } from "../../shared/api/errors";
import defaultAvatar from "../../shared/ui/defaultAvatar";
import { showToast } from "../../shared/ui/toast";
import SettingsNav from "./components/SettingsNav";

const CROP_PREVIEW_SIZE = 280;
const GENDER_VALUES = new Set(["UNSPECIFIED", "MALE", "FEMALE", "OTHER"]);

function normalizeGender(value) {
  const normalized = String(value || "UNSPECIFIED").toUpperCase();
  return GENDER_VALUES.has(normalized) ? normalized : "UNSPECIFIED";
}

function clampCropOffset(offset, dimensions, zoom) {
  if (!dimensions) return offset;
  const baseScale = Math.max(
    CROP_PREVIEW_SIZE / dimensions.width,
    CROP_PREVIEW_SIZE / dimensions.height,
  );
  const maxX = Math.max(0, (dimensions.width * baseScale * zoom - CROP_PREVIEW_SIZE) / 2);
  const maxY = Math.max(0, (dimensions.height * baseScale * zoom - CROP_PREVIEW_SIZE) / 2);
  return {
    x: Math.max(-maxX, Math.min(maxX, offset.x)),
    y: Math.max(-maxY, Math.min(maxY, offset.y)),
  };
}

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const sessionUser = useAuthStore((state) => state.user);
  const profileQuery = useQuery({ queryKey: ["settings", "profile"], queryFn: getCurrentUser, enabled: IS_API_MODE });
  const profile = profileQuery.data || sessionUser || {};
  const [form, setForm] = useState(null);
  const [cropSource, setCropSource] = useState(null);
  const [cropZoom, setCropZoom] = useState(1);
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
  const cropDragRef = useRef(null);
  const updateMutation = useMutation({
    mutationFn: updateCurrentUser,
    onSuccess: (updated) => {
      queryClient.setQueryData(["settings", "profile"], updated);
      setForm(null); showToast("资料保存成功");
    },
    onError: () => showToast("资料保存失败", "error"),
  });
  const avatarMutation = useMutation({
    mutationFn: uploadAvatar,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["settings", "profile"] }); showToast("头像上传成功"); },
    onError: () => showToast("头像上传失败", "error"),
  });
  const values = form || {
    nickname: profile.nickname || profile.username || "",
    gender: normalizeGender(profile.gender),
    signature: profile.signature || "",
  };
  const displayAvatar = profile.avatar_url || profile.avatarUrl;
  function chooseAvatar(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setCropSource({ file, url: URL.createObjectURL(file), dimensions: null });
    setCropZoom(1);
    setCropOffset({ x: 0, y: 0 });
    event.target.value = "";
  }

  function cancelCrop() {
    if (cropSource?.url) URL.revokeObjectURL(cropSource.url);
    setCropSource(null);
    cropDragRef.current = null;
  }

  function handleCropPointerDown(event) {
    event.currentTarget.setPointerCapture(event.pointerId);
    cropDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: cropOffset.x,
      offsetY: cropOffset.y,
    };
  }

  function handleCropPointerMove(event) {
    const drag = cropDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setCropOffset(clampCropOffset({
      x: drag.offsetX + event.clientX - drag.startX,
      y: drag.offsetY + event.clientY - drag.startY,
    }, cropSource?.dimensions, cropZoom));
  }

  function handleCropPointerEnd(event) {
    if (cropDragRef.current?.pointerId === event.pointerId) cropDragRef.current = null;
  }

  function changeCropZoom(nextZoom) {
    setCropZoom(nextZoom);
    setCropOffset((current) => clampCropOffset(current, cropSource?.dimensions, nextZoom));
  }

  function confirmCrop() {
    const image = new Image();
    image.onload = () => {
      const baseScale = Math.max(CROP_PREVIEW_SIZE / image.width, CROP_PREVIEW_SIZE / image.height);
      const totalScale = baseScale * cropZoom;
      const sourceSize = CROP_PREVIEW_SIZE / totalScale;
      const sourceX = (image.width - sourceSize) / 2 - cropOffset.x / totalScale;
      const sourceY = (image.height - sourceSize) / 2 - cropOffset.y / totalScale;
      const canvas = document.createElement("canvas");
      canvas.width = 512; canvas.height = 512;
      canvas.getContext("2d").drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, 512, 512);
      canvas.toBlob((blob) => { avatarMutation.mutate(new File([blob], "avatar.png", { type: "image/png" })); setCropSource(null); URL.revokeObjectURL(cropSource.url); }, "image/png");
    };
    image.src = cropSource.url;
  }

  return (
    <section className="settings-page">
      <SettingsNav />
      <div className="settings-content">
        <div className="settings-panel">
          <h2>编辑个人资料</h2>
          <p className="settings-description">更新你的公开昵称和个人简介。</p>
          <div className="settings-form">
            <div className="settings-field settings-avatar-field"><div><img src={displayAvatar && !displayAvatar.includes("images.unsplash.com") ? displayAvatar : defaultAvatar} alt="当前头像" /><span className="settings-user-meta"><strong>{profile.username || ""}</strong><small>{profile.nickname || profile.username || ""}</small></span></div><label className="secondary-button">更换头像<input type="file" accept="image/*" hidden onChange={chooseAvatar} /></label></div>
            <div className="settings-field"><label><span>昵称</span><input value={values.nickname} onChange={(event) => setForm({ ...values, nickname: event.target.value })} required /></label></div>
            <div className="settings-field"><label><span>性别</span><select value={values.gender} onChange={(event) => setForm({ ...values, gender: event.target.value })}><option value="UNSPECIFIED">不设置</option><option value="MALE">男</option><option value="FEMALE">女</option><option value="OTHER">其他</option></select></label></div>
            <div className="settings-field"><label><span>个人简介</span><textarea rows="5" maxLength={120} value={values.signature} onChange={(event) => setForm({ ...values, signature: event.target.value })} /><small className="settings-counter">{values.signature.length}/120</small></label></div>
            {updateMutation.isError ? <p className="form-error">{getApiErrorMessage(updateMutation.error, "资料保存失败")}</p> : null}
            <button className="primary-button" type="button" disabled={updateMutation.isPending} onClick={() => updateMutation.mutate(values)}>{updateMutation.isPending ? "保存中……" : "保存更改"}</button>
          </div>
          {cropSource ? (
            <div className="avatar-crop-modal">
              <div className="avatar-crop-card">
                <h3>裁剪头像</h3>
                <div
                  className="avatar-crop-preview"
                  onPointerDown={handleCropPointerDown}
                  onPointerMove={handleCropPointerMove}
                  onPointerUp={handleCropPointerEnd}
                  onPointerCancel={handleCropPointerEnd}
                >
                  <img
                    src={cropSource.url}
                    style={cropSource.dimensions ? {
                      width: cropSource.dimensions.width * Math.max(
                        CROP_PREVIEW_SIZE / cropSource.dimensions.width,
                        CROP_PREVIEW_SIZE / cropSource.dimensions.height,
                      ),
                      height: cropSource.dimensions.height * Math.max(
                        CROP_PREVIEW_SIZE / cropSource.dimensions.width,
                        CROP_PREVIEW_SIZE / cropSource.dimensions.height,
                      ),
                      transform: `translate(-50%, -50%) translate(${cropOffset.x}px, ${cropOffset.y}px) scale(${cropZoom})`,
                    } : undefined}
                    onLoad={(event) => {
                      const dimensions = {
                        width: event.currentTarget.naturalWidth,
                        height: event.currentTarget.naturalHeight,
                      };
                      setCropSource((current) => current ? { ...current, dimensions } : current);
                      setCropOffset((current) => clampCropOffset(current, dimensions, cropZoom));
                    }}
                    draggable="false"
                    alt="头像裁剪预览"
                  />
                </div>
                <p className="avatar-crop-hint">拖动图片调整位置，滑动下方控制缩放</p>
                <input type="range" min="1" max="3" step="0.1" value={cropZoom} onChange={(event) => changeCropZoom(Number(event.target.value))} />
                <div>
                  <button type="button" onClick={cancelCrop}>取消</button>
                  <button className="primary-button" type="button" onClick={confirmCrop}>使用此头像</button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
